import React from "react";
import {
  Page, Text, View, Document, StyleSheet, Image,
} from "@react-pdf/renderer";
import {
  transformCitationLanguage,
  getLocalDate,
  parseDateWithoutTimezone,
  getLookupDescriptionByCode,
  getLookupByCode,
} from "../../../utilities/helpers";
import {
  COVERAGE_CHOICE_CITATION_GUID,
  COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID,
} from "../../../utilities/constants";

// ─── Strip HTML to plain text ────────────────────────────────────────────────
const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const fmt = (date) =>
  date ? getLocalDate(parseDateWithoutTimezone(date)) : "-";

// ─── Styles ───────────────────────────────────────────────────────────────────
const c = {
  navy: "#1a3557",
  blue: "#2563a8",
  lightBlue: "#dbeafe",
  border: "#c8d8ea",
  lightGray: "#f3f6fa",
  midGray: "#6b7280",
  dark: "#111827",
  white: "#ffffff",
  accent: "#e63946",
};

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9,
    color: c.dark,
    paddingTop: 40,
    paddingBottom: 52,
    paddingHorizontal: 40,
    lineHeight: 1.35,
  },

  // ── Cover ──
  coverPage: {
    fontFamily: "Helvetica",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingHorizontal: 56,
    paddingVertical: 64,
    backgroundColor: c.navy,
    color: c.white,
  },
  coverBadge: {
    fontSize: 8,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: "#93c5fd",
    marginBottom: 10,
  },
  coverTitle: {
    fontSize: 26,
    fontFamily: "Helvetica-Bold",
    color: c.white,
    marginBottom: 4,
    lineHeight: 1.2,
  },
  coverSub: {
    fontSize: 12,
    color: "#93c5fd",
    marginBottom: 32,
  },
  coverRule: {
    borderBottom: `3px solid ${c.accent}`,
    marginBottom: 28,
  },
  coverGrid: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  coverCell: {
    width: "50%",
    marginBottom: 14,
  },
  coverLabel: {
    fontSize: 7,
    color: "#93c5fd",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 2,
  },
  coverValue: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: c.white,
  },

  // ── Header (repeating) ──
  pageHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1.5px solid ${c.navy}`,
    paddingBottom: 5,
    marginBottom: 12,
  },
  pageHeaderTitle: {
    flex: 1,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: c.navy,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginRight: 8,
  },
  pageHeaderMeta: {
    fontSize: 7,
    color: c.midGray,
    flexShrink: 0,
  },

  // ── Footer ──
  pageFooter: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: `1px solid ${c.border}`,
    paddingTop: 4,
  },
  footerText: {
    fontSize: 7,
    color: c.midGray,
  },

  // ── Section heading ──
  sectionHeading: {
    backgroundColor: c.navy,
    color: c.white,
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1,
    padding: "6 10",
    marginBottom: 8,
    marginTop: 12,
  },

  // ── Sub-heading ──
  subHeading: {
    backgroundColor: c.lightBlue,
    color: c.navy,
    fontFamily: "Helvetica-Bold",
    fontSize: 8.5,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    padding: "4 8",
    marginBottom: 6,
    marginTop: 8,
  },

  // ── Two-col info grid ──
  infoGrid: { display: "flex", flexDirection: "row", flexWrap: "wrap", marginBottom: 8 },
  infoCell: { width: "50%", marginBottom: 5 },
  infoLabel: { fontSize: 7, color: c.midGray, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 1 },
  infoValue: { fontSize: 8.5, color: c.dark },

  // ── Body text ──
  bodyText: { fontSize: 8.5, color: c.dark, lineHeight: 1.45, marginBottom: 4 },

  // ── Observation / Discussion item ──
  itemBox: {
    border: `1px solid ${c.border}`,
    borderRadius: 3,
    padding: "7 9",
    marginBottom: 7,
    backgroundColor: c.white,
  },
  itemNumber: {
    backgroundColor: c.blue,
    color: c.white,
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    padding: "2 7",
    marginBottom: 5,
    alignSelf: "flex-start",
  },
  itemTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: c.dark,
    marginBottom: 5,
    lineHeight: 1.3,
  },
  fieldLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 7.5,
    color: c.navy,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginTop: 5,
    marginBottom: 2,
  },
  fieldText: { fontSize: 8.5, lineHeight: 1.4, color: "#374151" },

  // ── TOC ──
  tocRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: `1px dotted ${c.border}`,
    paddingVertical: 3,
  },
  tocLabel: { fontSize: 9, color: c.dark },
  tocDots: { flex: 1, borderBottom: `1px dotted ${c.border}`, marginHorizontal: 4, marginBottom: 3 },

  // ── Signature ──
  sigBlock: { marginBottom: 10, padding: "6 8", border: `1px solid ${c.border}`, borderRadius: 2 },
  sigName: { fontSize: 8.5, fontFamily: "Helvetica-Bold", marginBottom: 3 },
  sigImg: { width: 120, height: 32, marginBottom: 3 },
  sigLine: { borderBottom: `1px solid ${c.dark}`, width: 160, marginBottom: 2 },

  // ── Table ──
  tableHeader: { display: "flex", flexDirection: "row", backgroundColor: c.navy, padding: "4 6" },
  tableRow: { display: "flex", flexDirection: "row", borderBottom: `1px solid ${c.border}`, padding: "3 6" },
  tableColNum: { width: "8%", fontSize: 8, color: c.white, fontFamily: "Helvetica-Bold" },
  tableColDesc: { width: "60%", fontSize: 8, color: c.white, fontFamily: "Helvetica-Bold" },
  tableColRef: { width: "32%", fontSize: 8, color: c.white, fontFamily: "Helvetica-Bold" },
  tableColNumD: { width: "8%", fontSize: 8 },
  tableColDescD: { width: "60%", fontSize: 8 },
  tableColRefD: { width: "32%", fontSize: 8, color: c.midGray },
});

// ─── Reusable sub-components ─────────────────────────────────────────────────

const PageHeader = ({ firmName, inspectionDates }) => (
  <View style={styles.pageHeader} fixed>
    <Text style={styles.pageHeaderTitle}>Establishment Inspection Report — {firmName}</Text>
    <Text style={styles.pageHeaderMeta}>{inspectionDates}</Text>
  </View>
);

const PageFooter = ({ firmName }) => (
  <View style={styles.pageFooter} fixed>
    <Text style={styles.footerText}>Wynngate Corporation — Confidential</Text>
    <Text style={styles.footerText} render={({ pageNumber, totalPages }) =>
      `Page ${pageNumber} of ${totalPages}`
    } />
  </View>
);

const SectionHead = ({ children }) => (
  <View style={styles.sectionHeading} wrap={false}><Text>{children}</Text></View>
);

const SubHead = ({ children }) => (
  <View style={styles.subHeading} wrap={false}><Text>{children}</Text></View>
);

const InfoGrid = ({ rows }) => (
  <View style={styles.infoGrid}>
    {rows.map(([label, value]) => (
      <View key={label} style={styles.infoCell}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue}>{value || "—"}</Text>
      </View>
    ))}
  </View>
);

// Render multi-line text as one <Text> per line. A single <Text> containing
// embedded "\n" makes @react-pdf miscalculate height, causing following
// content to overlap it. Splitting per line keeps the layout correct.
const RichText = ({ text, style }) =>
  text.split("\n").map((line, i) =>
    line.trim()
      ? <Text key={i} style={style}>{line}</Text>
      : <View key={i} style={{ height: 4 }} />
  );

const FieldBlock = ({ label, html }) => {
  const text = stripHtml(html);
  if (!text) return null;
  return (
    <View style={{ marginBottom: 4 }}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <RichText text={text} style={styles.fieldText} />
    </View>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const EIRReportPdf = ({
  assignment,
  inspection,
  inspectionDetails,
  lookups,
  lookupsByGeneral,
  b503Operations,
  a503Operations,
  inspectionExhibitions,
  allowedAssignTos,
}) => {
  const auditorName = assignment?.auditing_firm_name || "—";
  const auditedName = assignment?.audited_firm_name || "—";

  const dates = (assignment?.inspection_dates || []);
  const datesSorted = [...dates].map(d => parseDateWithoutTimezone(d));
  const startDate = dates.length ? fmt(dates.reduce((a, b) =>
    parseDateWithoutTimezone(a) < parseDateWithoutTimezone(b) ? a : b
  )) : "—";
  const endDate = dates.length ? fmt(dates.reduce((a, b) =>
    parseDateWithoutTimezone(a) > parseDateWithoutTimezone(b) ? a : b
  )) : "—";
  const inspectionDatesStr = datesSorted.map(d => getLocalDate(d)).join(", ");

  // ── General sections (SM codes) ────────────────────────────────────────────
  const smQualities = (inspection?.protocol_quality || [])
    .filter(pq => pq?.code?.startsWith("SM"))
    .sort((a, b) => a.code.localeCompare(b.code));

  const generalGroups = (lookupsByGeneral || [])
    .filter(l => l.parent_uid === null)
    .filter(l => !l.description.toLowerCase().includes("system choice"))
    .sort((a, b) => {
      const priority = ["summary", "administrative data"];
      const ai = priority.findIndex(p => a.description.toLowerCase().includes(p));
      const bi = priority.findIndex(p => b.description.toLowerCase().includes(p));
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.code.localeCompare(b.code);
    });

  // ── Observations ────────────────────────────────────────────────────────────
  const systemChoicesObs = inspection?.system_choice_observation || [];
  const observationItems = [];
  systemChoicesObs.forEach(sc => {
    const items = (inspectionDetails || [])
      .filter(d =>
        d.detail?.coverageChoice === COVERAGE_CHOICE_CITATION_GUID &&
        d.detail?.systemChoice === sc.code_table_uid
      )
      .sort((a, b) => (a.detail?.sortOrder ?? 999) - (b.detail?.sortOrder ?? 999));
    observationItems.push(...items);
  });
  // 503B/A observations
  (b503Operations || [])
    .filter(op => op.detail?.coverageChoice === COVERAGE_CHOICE_CITATION_GUID)
    .sort((a, b) => (a.detail?.sortOrder ?? 999) - (b.detail?.sortOrder ?? 999))
    .forEach(op => observationItems.push({
      code: op.code,
      detail: {
        citationLanguage: op.description || op.code,
        citationSpecifically: op.detail?.citationSpecifically || "",
        citationSupporting: op.detail?.citationSupporting || "",
        citationDiscussion: op.detail?.citationDiscussion || "",
        citationCapa: op.detail?.citationCapa || "",
      }
    }));
  (a503Operations || [])
    .filter(op => op.detail?.coverageChoice === COVERAGE_CHOICE_CITATION_GUID)
    .sort((a, b) => (a.detail?.sortOrder ?? 999) - (b.detail?.sortOrder ?? 999))
    .forEach(op => observationItems.push({
      code: op.code,
      detail: {
        citationLanguage: op.description || op.code,
        citationSpecifically: op.detail?.citationSpecifically || "",
        citationSupporting: op.detail?.citationSupporting || "",
        citationDiscussion: op.detail?.citationDiscussion || "",
        citationCapa: op.detail?.citationCapa || "",
      }
    }));

  // ── Discussions ─────────────────────────────────────────────────────────────
  const systemChoicesDsc = inspection?.system_choice_discussion || [];
  const discussionItems = [];
  systemChoicesDsc.forEach(sc => {
    const items = (inspectionDetails || [])
      .filter(d =>
        d.detail?.coverageChoice === COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID &&
        d.detail?.systemChoice === sc.code_table_uid
      )
      .sort((a, b) => (a.detail?.sortOrder ?? 999) - (b.detail?.sortOrder ?? 999));
    discussionItems.push(...items);
  });
  (b503Operations || [])
    .filter(op => op.detail?.coverageChoice === COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID)
    .sort((a, b) => (a.detail?.sortOrder ?? 999) - (b.detail?.sortOrder ?? 999))
    .forEach(op => discussionItems.push({
      code: op.code,
      detail: {
        citationLanguage: op.description || op.code,
        citationSpecifically: op.detail?.citationSpecifically || "",
        citationSupporting: op.detail?.citationSupporting || "",
        citationDiscussion: op.detail?.citationDiscussion || "",
        citationCapa: op.detail?.citationCapa || "",
      }
    }));
  (a503Operations || [])
    .filter(op => op.detail?.coverageChoice === COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID)
    .sort((a, b) => (a.detail?.sortOrder ?? 999) - (b.detail?.sortOrder ?? 999))
    .forEach(op => discussionItems.push({
      code: op.code,
      detail: {
        citationLanguage: op.description || op.code,
        citationSpecifically: op.detail?.citationSpecifically || "",
        citationSupporting: op.detail?.citationSupporting || "",
        citationDiscussion: op.detail?.citationDiscussion || "",
        citationCapa: op.detail?.citationCapa || "",
      }
    }));

  // ── Exhibits ────────────────────────────────────────────────────────────────
  const exhibits = Array.isArray(inspectionExhibitions) ? inspectionExhibitions : [];

  // ── Participants ────────────────────────────────────────────────────────────
  const participants = (allowedAssignTos || []).filter(p =>
    p.roleName?.toLowerCase() !== "supervisor"
  );

  return (
    <Document
      title={`EIR - ${auditedName}`}
      author="Wynngate Corporation"
      subject="Establishment Inspection Report"
    >
      {/* ── COVER PAGE ─────────────────────────────────────────────────────── */}
      <Page size="A4" style={styles.coverPage}>
        <Text style={styles.coverBadge}>Confidential — FDA Review</Text>
        <Text style={styles.coverTitle}>Establishment{"\n"}Inspection Report</Text>
        <Text style={styles.coverSub}>EIR</Text>
        <View style={styles.coverRule} />
        <View style={styles.coverGrid}>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>Audited Firm</Text>
            <Text style={styles.coverValue}>{auditedName}</Text>
          </View>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>Auditing Firm</Text>
            <Text style={styles.coverValue}>{auditorName}</Text>
          </View>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>Facility Address</Text>
            <Text style={styles.coverValue}>{assignment?.audited_firm_address || "—"}</Text>
          </View>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>Establishment Type</Text>
            <Text style={styles.coverValue}>{assignment?.audited_establishment_type || "—"}</Text>
          </View>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>Start Date</Text>
            <Text style={styles.coverValue}>{startDate}</Text>
          </View>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>End Date</Text>
            <Text style={styles.coverValue}>{endDate}</Text>
          </View>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>Days in Facility</Text>
            <Text style={styles.coverValue}>{dates.length}</Text>
          </View>
          <View style={styles.coverCell}>
            <Text style={styles.coverLabel}>Prepared By</Text>
            <Text style={styles.coverValue}>Wynngate Corporation</Text>
          </View>
        </View>
        <View style={{ marginTop: 32 }}>
          <Text style={{ ...styles.coverLabel, marginBottom: 4 }}>Auditors</Text>
          {participants.map(p => (
            <Text key={p.user_name} style={{ fontSize: 9, color: "#dbeafe", marginBottom: 2 }}>
              {p.user_name}{p.roleName ? ` — ${p.roleName}` : ""}
            </Text>
          ))}
        </View>
      </Page>

      {/* ── BODY PAGES ─────────────────────────────────────────────────────── */}
      <Page size="A4" style={styles.page}>
        <PageHeader firmName={auditedName} inspectionDates={inspectionDatesStr} />
        <PageFooter firmName={auditedName} />

        {/* ── Administrative Data ──────────────────────────────────────────── */}
        <SectionHead>Administrative Data</SectionHead>
        <InfoGrid rows={[
          ["Audited Firm", assignment?.audited_firm_name],
          ["Auditing Firm", assignment?.auditing_firm_name],
          ["Facility Address", assignment?.audited_firm_address],
          ["Auditing Firm Address", assignment?.auditing_firm_address],
          ["Phone", assignment?.audited_firm_phone || assignment?.auditing_firm_phone],
          ["Fax", assignment?.audited_firm_fax || assignment?.auditing_firm_fax],
          ["Dates of Inspection", inspectionDatesStr],
          ["Days in Facility", String(dates.length)],
          ["Main Contact", assignment?.audited_main_contact],
          ["Establishment Type", assignment?.audited_establishment_type],
        ]} />

        {participants.length > 0 && (
          <>
            <SubHead>Auditors</SubHead>
            {participants.map(p => (
              <Text key={p.user_name} style={styles.bodyText}>
                {p.user_name}{p.roleName ? ` — ${p.roleName}` : ""}
              </Text>
            ))}
          </>
        )}

        {/* ── General Sections (SM codes) ──────────────────────────────────── */}
        {generalGroups.map(group => {
          const groupItems = smQualities.filter(pq => {
            const lk = getLookupByCode(lookups, pq.code);
            return lk?.parent_uid === group.lookup_uid;
          });
          const itemsWithContent = groupItems.filter(pq => {
            const detail = (inspectionDetails || []).find(d =>
              d.inspection_detail_uid === pq.inpsection_detail_uid || d.code === pq.code
            )?.detail || pq.detail;
            return stripHtml(detail?.coverageComment);
          });
          if (itemsWithContent.length === 0) return null;

          return (
            <View key={group.lookup_uid}>
              <SectionHead>{group.description}</SectionHead>
              {itemsWithContent.map(pq => {
                const detail = (inspectionDetails || []).find(d =>
                  d.inspection_detail_uid === pq.inpsection_detail_uid || d.code === pq.code
                )?.detail || pq.detail;
                const text = stripHtml(detail?.coverageComment);
                if (!text) return null;
                return (
                  <View key={pq.code} style={{ marginBottom: 6 }}>
                    <SubHead>{getLookupDescriptionByCode(lookups, pq.code) || pq.code}</SubHead>
                    <RichText text={text} style={styles.bodyText} />
                  </View>
                );
              })}
            </View>
          );
        })}

        {/* ── Observations / Major Findings ────────────────────────────────── */}
        {observationItems.length > 0 && (
          <>
            <SectionHead>Observations / Major Findings</SectionHead>
            <Text style={{ ...styles.bodyText, marginBottom: 6, fontFamily: "Helvetica-Oblique" }}>
              During an audit of your firm, I/we observed:
            </Text>
            {observationItems.map((item, idx) => (
              <View key={item.code || idx} style={styles.itemBox}>
                <View wrap={false}>
                  <Text style={styles.itemNumber}>OBSERVATION {idx + 1}</Text>
                  <Text style={styles.itemTitle}>
                    {transformCitationLanguage(item.detail?.citationLanguage)}
                  </Text>
                </View>
                <FieldBlock label="Specifically" html={item.detail?.citationSpecifically} />
                <FieldBlock label="Supporting Evidence" html={item.detail?.citationSupporting} />
                <FieldBlock label="Recommendation" html={item.detail?.citationDiscussion} />
                <FieldBlock label="CAPA" html={item.detail?.citationCapa} />
              </View>
            ))}
          </>
        )}

        {/* ── Discussions / Minor Findings ─────────────────────────────────── */}
        {discussionItems.length > 0 && (
          <>
            <SectionHead>Discussions / Minor Findings</SectionHead>
            <Text style={{ ...styles.bodyText, marginBottom: 6, fontFamily: "Helvetica-Oblique" }}>
              During an audit of your firm, I/we discussed:
            </Text>
            {discussionItems.map((item, idx) => (
              <View key={item.code || idx} style={styles.itemBox}>
                <View wrap={false}>
                  <Text style={styles.itemNumber}>DISCUSSION {idx + 1}</Text>
                  <Text style={styles.itemTitle}>
                    {transformCitationLanguage(item.detail?.citationLanguage)}
                  </Text>
                </View>
                <FieldBlock label="Specifically" html={item.detail?.citationSpecifically} />
                <FieldBlock label="Supporting Evidence" html={item.detail?.citationSupporting} />
                <FieldBlock label="Recommendation" html={item.detail?.citationDiscussion} />
                <FieldBlock label="CAPA" html={item.detail?.citationCapa} />
              </View>
            ))}
          </>
        )}

        {/* ── Exhibits ─────────────────────────────────────────────────────── */}
        {exhibits.length > 0 && (
          <>
            <SectionHead>Exhibits</SectionHead>
            <View style={styles.tableHeader} wrap={false}>
              <Text style={styles.tableColNum}>#</Text>
              <Text style={styles.tableColDesc}>Description</Text>
              <Text style={styles.tableColRef}>Reference</Text>
            </View>
            {exhibits.map((ex, idx) => (
              <View key={ex.inspection_exhibition_uid || idx} style={styles.tableRow} wrap={false}>
                <Text style={styles.tableColNumD}>{idx + 1}</Text>
                <Text style={styles.tableColDescD}>{ex.description || ex.name || ex.file_name || `Exhibit ${idx + 1}`}</Text>
                <Text style={styles.tableColRefD}>{ex.reference || ""}</Text>
              </View>
            ))}
          </>
        )}

        {/* ── Signatures ───────────────────────────────────────────────────── */}
        <SectionHead>Auditor Signatures</SectionHead>
        {participants.map(p => (
          <View key={p.user_name} style={styles.sigBlock} wrap={false}>
            <Text style={styles.sigName}>{p.user_name}{p.roleName ? ` — ${p.roleName}` : ""}</Text>
            {p.signature_url
              ? <Image style={styles.sigImg} src={p.signature_url} />
              : <View style={styles.sigLine} />
            }
          </View>
        ))}

      </Page>
    </Document>
  );
};

export default EIRReportPdf;
